import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUp,
  faBookmark,
  faChevronDown,
  faCommentDots,
  faCompactDisc,
  faDesktop,
  faHeart,
  faHouse,
  faInbox,
  faMagnifyingGlass,
  faPlus,
  faPlay,
  faRotate,
  faShare,
  faStar,
  faUser,
  faUsers,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

const JEV_STATES = Object.freeze({
  KEEP: 'KEEP',
  SAVE: 'SAVE',
  SKIP: 'SKIP',
  HUMAN_REVIEW: 'HUMAN_REVIEW',
});

const actionColors = {
  [JEV_STATES.SAVE]: 'yellow',
  [JEV_STATES.KEEP]: 'blue',
  [JEV_STATES.SKIP]: 'red',
  [JEV_STATES.HUMAN_REVIEW]: 'amber',
};

const JEV_PROXY_URL = process.env.REACT_APP_JEV_PROXY_URL ?? '';

export default function DuoPage({ videos }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isPlaying, setIsPlaying] = useState(true);
  const [likedIds, setLikedIds] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [showHeart, setShowHeart] = useState(false);
  const [decisionLog, setDecisionLog] = useState([]);
  const [liveDecisions, setLiveDecisions] = useState({});
  const [apiStatus, setApiStatus] = useState('fixture');
  const [apiError, setApiError] = useState('');
  const [automationEnabled, setAutomationEnabled] = useState(false);
  const [autoSkip, setAutoSkip] = useState(true);
  const [autoSave, setAutoSave] = useState(true);
  const [autoKeep, setAutoKeep] = useState(true);
  const [actionFeedback, setActionFeedback] = useState('');
  const videoRef = useRef(null);
  const gestureRef = useRef(null);
  const clickTimerRef = useRef(null);
  const heartTimerRef = useRef(null);
  const wheelLockRef = useRef(false);
  const suppressClickRef = useRef(false);
  const actionTimerRef = useRef(null);
  const decisionRequestRef = useRef(0);
  const currentVideo = videos[currentIndex];
  const decisionReady = Boolean(liveDecisions[currentVideo.id]);
  const displayedDecision = liveDecisions[currentVideo.id] || currentVideo.decision;

  const moveVideo = (direction) => {
    decisionRequestRef.current += 1;
    window.clearTimeout(actionTimerRef.current);
    setCommentsOpen(false);
    setIsThinking(false);
    setApiStatus('fixture');
    setIsPlaying(true);
    setActionFeedback('');
    setCurrentIndex((index) => (index + direction + videos.length) % videos.length);
  };

  const setPlayback = (playing) => {
    if (videoRef.current) {
      if (playing) videoRef.current.play().catch(() => {});
      else videoRef.current.pause();
    }
    setIsPlaying(playing);
  };

  const likeVideo = () => {
    setLikedIds((items) => (items.includes(currentVideo.id) ? items : [...items, currentVideo.id]));
    setShowHeart(true);
    window.clearTimeout(heartTimerRef.current);
    heartTimerRef.current = window.setTimeout(() => setShowHeart(false), 620);
  };

  const scheduleNextVideo = (message, delay = 760) => {
    setActionFeedback(message);
    window.clearTimeout(actionTimerRef.current);
    actionTimerRef.current = window.setTimeout(() => moveVideo(1), delay);
  };

  const executeDecision = (decision, automatic = false) => {
    if (decision.action === JEV_STATES.SAVE) {
      if (automatic && !autoSave) {
        setActionFeedback('SAVE 已识别 · 自动收藏已关闭');
        return;
      }
      setSavedIds((items) => (items.includes(currentVideo.id) ? items : [...items, currentVideo.id]));
      setActionFeedback('已收藏 · 只执行收藏，不切换视频');
      return;
    }

    if (decision.action === JEV_STATES.SKIP) {
      if (automatic && !autoSkip) {
        setActionFeedback('SKIP 已识别 · 自动跳过已关闭');
        return;
      }
      scheduleNextVideo('已跳过 · 左屏正在进入下一条');
      return;
    }

    if (decision.action === JEV_STATES.KEEP) {
      if (automatic && !autoKeep) {
        setActionFeedback('KEEP 已识别 · 自动认真看已关闭');
        return;
      }
      setPlayback(true);
      setActionFeedback('继续观看 · 左屏保持播放，不切换视频');
      return;
    }

    setPlayback(false);
    setActionFeedback('交给人判断 · 左屏已停住');
  };

  const runDecision = async (automatic = false) => {
    if (isThinking) return;
    const requestId = decisionRequestRef.current + 1;
    decisionRequestRef.current = requestId;
    const videoSnapshot = currentVideo;
    setIsThinking(true);
    setApiStatus('thinking');
    setApiError('');
    try {
      const response = await fetch(`${JEV_PROXY_URL}/api/jev/decide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: currentVideo.title,
          description: currentVideo.description,
          username: currentVideo.username,
          topComment: currentVideo.topComment,
          comments: currentVideo.commentsData,
        }),
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.error || `Jev proxy returned HTTP ${response.status}`);
      if (requestId !== decisionRequestRef.current) return;
      const decision = {
        ...payload.decision,
        latency: payload.latency_ms,
        scores: currentVideo.decision.scores,
        source: 'live',
      };
      setLiveDecisions((items) => ({ ...items, [videoSnapshot.id]: decision }));
      setDecisionLog((items) => [{
        id: `${videoSnapshot.id}-${Date.now()}`,
        title: videoSnapshot.title,
        action: decision.action,
        label: decision.label,
        confidence: decision.confidence,
        latency: decision.latency,
      }, ...items].slice(0, 7));
      setApiStatus('live');
      setIsThinking(false);
      executeDecision(decision, automatic);
    } catch (error) {
      if (requestId !== decisionRequestRef.current) return;
      setApiStatus('error');
      setApiError(error.message);
      setIsThinking(false);
    }
  };

  useEffect(() => {
    if (!automationEnabled || commentsOpen || isThinking) return undefined;
    const timer = window.setTimeout(() => runDecision(true), 620);
    return () => window.clearTimeout(timer);
    // The current video is the intentional trigger for this small state machine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationEnabled, currentIndex, commentsOpen]);

  const handleStagePointerDown = (event) => {
    if (event.target.closest('button')) return;
    gestureRef.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleStagePointerUp = (event) => {
    if (!gestureRef.current || event.target.closest('button')) return;
    const start = gestureRef.current;
    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    gestureRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (Math.hypot(deltaX, deltaY) < 55 || Math.abs(deltaY) < Math.abs(deltaX) * 1.15) return;
    event.preventDefault();
    suppressClickRef.current = true;
    window.setTimeout(() => { suppressClickRef.current = false; }, 300);
    window.clearTimeout(clickTimerRef.current);
    moveVideo(deltaY < 0 ? 1 : -1);
    const stage = event.currentTarget;
    stage.classList.remove('gesture-next', 'gesture-prev');
    stage.classList.add(deltaY < 0 ? 'gesture-next' : 'gesture-prev');
    window.setTimeout(() => stage.classList.remove('gesture-next', 'gesture-prev'), 240);
  };

  const handleStageClick = (event) => {
    if (event.target.closest('button') || suppressClickRef.current || gestureRef.current) return;
    window.clearTimeout(clickTimerRef.current);
    clickTimerRef.current = window.setTimeout(() => setPlayback(!isPlaying), 220);
  };

  const handleStageWheel = (event) => {
    if (Math.abs(event.deltaY) < 22 || wheelLockRef.current) return;
    event.preventDefault();
    wheelLockRef.current = true;
    moveVideo(event.deltaY > 0 ? 1 : -1);
    window.setTimeout(() => { wheelLockRef.current = false; }, 420);
  };

  const toggleSaved = () => setSavedIds((items) => (
    items.includes(currentVideo.id) ? items.filter((id) => id !== currentVideo.id) : [...items, currentVideo.id]
  ));

  const stats = {
    scanned: decisionLog.length,
    skipped: decisionLog.filter((item) => item.action === JEV_STATES.SKIP).length,
    saved: savedIds.length,
    human: decisionLog.filter((item) => item.action === JEV_STATES.HUMAN_REVIEW).length,
  };

  return (
    <main className="duo-route">
      <header className="duo-route-header">
        <div>
          <a className="duo-back-link" href="/">← SINGLE FEED</a>
          <div className="duo-kicker"><FontAwesomeIcon icon={faStar} /> JEVTOK / DUO MODE</div>
          <h1>一台手机，左眼刷视频，右眼做决定。</h1>
          <p>同一个视频、同一组最高赞评论，左边是人的信息流，右边是 Jev 的行动状态。</p>
        </div>
        <div className="duo-route-spec"><span>OPENED DEVICE</span><strong>DUO / 7.6″ INNER DISPLAY</strong><small>FEED + DECISION / ONE CONTINUOUS SURFACE</small></div>
      </header>

      <section className="duo-device" aria-label="Duo 双屏模拟器">
        <div className="duo-edge-label duo-edge-label-left">TITANIUM / 01</div>
        <div className="duo-edge-label duo-edge-label-right">JEV / 02</div>
        <div className="duo-pane duo-feed-pane">
          <DuoTikTokScreen
            video={currentVideo}
            currentIndex={currentIndex}
            total={videos.length}
            isPlaying={isPlaying}
            liked={likedIds.includes(currentVideo.id)}
            saved={savedIds.includes(currentVideo.id)}
            showHeart={showHeart}
            commentsOpen={commentsOpen}
            videoRef={videoRef}
            onPointerDown={handleStagePointerDown}
            onPointerUp={handleStagePointerUp}
            onPointerCancel={() => { gestureRef.current = null; }}
            onClick={handleStageClick}
            onDoubleClick={(event) => { if (!event.target.closest('button')) { window.clearTimeout(clickTimerRef.current); likeVideo(); } }}
            onWheel={handleStageWheel}
            onLike={likeVideo}
            onSave={toggleSaved}
            onComments={() => setCommentsOpen(true)}
            onPrevious={() => moveVideo(-1)}
            onNext={() => moveVideo(1)}
            onCloseComments={() => setCommentsOpen(false)}
          />
        </div>
        <div className="duo-hinge" aria-hidden="true"><span /><i /></div>
        <div className="duo-pane duo-jev-pane-wrap">
      <DuoJevPane
            video={currentVideo}
            currentIndex={currentIndex}
            total={videos.length}
            decision={displayedDecision}
            decisionReady={decisionReady}
            isThinking={isThinking}
            apiStatus={apiStatus}
            apiError={apiError}
            decisionLog={decisionLog}
            stats={stats}
            automationEnabled={automationEnabled}
            autoSkip={autoSkip}
            autoSave={autoSave}
            autoKeep={autoKeep}
            actionFeedback={actionFeedback}
            onToggleAutomation={setAutomationEnabled}
            onToggleAutoSkip={setAutoSkip}
            onToggleAutoSave={setAutoSave}
            onToggleAutoKeep={setAutoKeep}
            onRun={() => runDecision(false)}
            onComments={() => setCommentsOpen(true)}
          />
        </div>
      </section>

      <footer className="duo-route-footer"><span>↑↓ / SWIPE LEFT SCREEN TO CHANGE VIDEO</span><span>DOUBLE TAP / LIKE</span><span>JEV OUTPUT / KEEP · SAVE · SKIP · HUMAN REVIEW</span></footer>
    </main>
  );
}

function DuoTikTokScreen({
  video, currentIndex, total, isPlaying, liked, saved, showHeart, commentsOpen, videoRef,
  onPointerDown, onPointerUp, onPointerCancel, onClick, onDoubleClick, onWheel, onLike,
  onSave, onComments, onPrevious, onNext, onCloseComments,
}) {
  return (
    <div className="duo-tiktok-screen">
      <div className="duo-screen-glass" />
      <div className="duo-statusbar"><span>9:41</span><span className="duo-dynamic-island" /><span>◔ ◔ ▰</span></div>
      <div className="feed-topbar"><span className="feed-desktop-icon"><FontAwesomeIcon icon={faDesktop} /></span><span>Following&nbsp;&nbsp; <b>For You</b></span><span className="feed-search-icon"><FontAwesomeIcon icon={faMagnifyingGlass} /></span></div>
      <div className="video-stage" onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel} onClick={onClick} onDoubleClick={onDoubleClick} onWheel={onWheel}>
        <video key={video.id} ref={videoRef} className="active-video" src={video.url} autoPlay={isPlaying} muted loop playsInline />
        <div className="video-vignette" />
        <div className={`heart-burst ${showHeart ? 'show' : ''}`} aria-hidden="true"><FontAwesomeIcon icon={faHeart} /></div>
        <div className="video-meta"><strong>{video.username}</strong><h2>{video.title}</h2><p>{video.description}</p><span className="music-line">♫ {video.song}</span></div>
        <div className="video-actions">
          <div className="creator-action"><div className="creator-avatar">{video.avatar}</div><button type="button" className="follow-plus" aria-label="关注"><FontAwesomeIcon icon={faPlus} /></button></div>
          <button type="button" className={liked ? 'is-liked' : ''} onClick={onLike} aria-label="点赞"><FontAwesomeIcon icon={faHeart} /><small>{video.likes}</small></button>
          <button type="button" onClick={onComments} aria-label="评论"><FontAwesomeIcon icon={faCommentDots} /><small>{video.comments}</small></button>
          <button type="button" className={saved ? 'is-saved' : ''} onClick={onSave} aria-label="收藏"><FontAwesomeIcon icon={faBookmark} /><small>{video.saves}</small></button>
          <button type="button" aria-label="分享"><FontAwesomeIcon icon={faShare} /><small>{video.shares}</small></button>
          <div className="music-disc"><FontAwesomeIcon icon={faCompactDisc} /></div>
        </div>
      </div>
      <div className="feed-bottom"><span className="feed-tab active"><FontAwesomeIcon icon={faHouse} /><small>Home</small></span><span className="feed-tab"><FontAwesomeIcon icon={faUsers} /><small>Friends</small></span><span className="create-button"><FontAwesomeIcon icon={faPlus} /></span><span className="feed-tab"><FontAwesomeIcon icon={faInbox} /><small>Inbox</small></span><span className="feed-tab"><FontAwesomeIcon icon={faUser} /><small>Profile</small></span></div>
      <div className="feed-arrows"><button type="button" onClick={onPrevious} aria-label="上一条"><FontAwesomeIcon icon={faChevronDown} rotation={180} /></button><button type="button" onClick={onNext} aria-label="下一条"><FontAwesomeIcon icon={faChevronDown} /></button></div>
      <div className="duo-video-index">{String(currentIndex + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</div>
      {commentsOpen && <DuoCommentsDrawer video={video} onClose={onCloseComments} />}
    </div>
  );
}

function DuoJevPane({ video, currentIndex, total, decision, decisionReady, isThinking, apiStatus, apiError, decisionLog, stats, automationEnabled, autoSkip, autoSave, autoKeep, actionFeedback, onToggleAutomation, onToggleAutoSkip, onToggleAutoSave, onToggleAutoKeep, onRun, onComments }) {
  const decisionColor = isThinking ? 'thinking' : decisionReady ? actionColors[decision.action] : 'waiting';
  const feedback = isThinking
    ? '正在读取评论最高赞信号'
    : decisionReady
      ? actionFeedback || ({ [JEV_STATES.SAVE]: '准备收藏', [JEV_STATES.SKIP]: '准备进入下一条', [JEV_STATES.KEEP]: '继续播放', [JEV_STATES.HUMAN_REVIEW]: '等待人工判断' }[decision.action] || '等待下一步')
      : '等待 Jev 读取评论后再决定';
  const statusLabel = apiStatus === 'live' ? 'LIVE API' : apiStatus === 'thinking' ? 'READING COMMENTS' : apiStatus === 'error' ? 'RETRY' : decisionReady ? 'DECISION READY' : 'READY TO READ';
  return (
    <div className="jev-panel duo-jev-panel">
      <div className="duo-jev-topline"><div><span className="status-dot" /> JEV / DECISION LAYER</div><span className={`replay-tag ${apiStatus === 'live' ? 'live' : ''}`}>{statusLabel}</span></div>
      <div className="duo-jev-intro"><div><span>THE OTHER HALF OF THE PHONE</span><strong>Jev 不解释视频，Jev 只决定下一步。</strong></div><div className="duo-jev-count">{String(currentIndex + 1).padStart(2, '0')}<small> / {String(total).padStart(2, '0')}</small></div></div>
      <div className="duo-input-card"><div className="duo-card-label"><span>READING NOW</span><span>{video.username}</span></div><h2>{video.title}</h2><p>{video.description}</p><div className="duo-top-comment"><FontAwesomeIcon icon={faCommentDots} /><div><small>TOP COMMENT / HIGHEST SIGNAL</small><strong>{video.topComment}</strong></div></div><button type="button" className="duo-comments-link" onClick={onComments}>打开左屏评论区 <FontAwesomeIcon icon={faArrowUp} rotation={45} /></button></div>
      <div className="duo-decision-card">
        <div className="duo-card-label"><span>JEV OUTPUT STATE</span><span>{isThinking ? 'THINKING' : `${decision.latency} ms`}</span></div>
        <div className={`decision-main ${decisionColor}`}>{decisionReady && decision.action === JEV_STATES.SAVE && <span className="decision-star-badge"><FontAwesomeIcon icon={faStar} /></span>}<strong>{isThinking ? '正在读评论…' : decisionReady ? decision.label : '等待 Jev 判断'}</strong><span>{isThinking ? 'OBSERVE → EVALUATE' : decisionReady ? `${decision.action} · ${Math.round(decision.confidence * 100)}%` : 'COMMENTS → DECIDE'}</span></div>
        <div className="duo-state-machine"><span>最高赞评论</span><i>→</i><span>JEV</span><i>→</i><b>{isThinking ? '判断中' : decisionReady ? decision.action : '等待'}</b></div>
        <div className="state-path"><span>OBSERVE</span><i>→</i><span>COMMENTS</span><i>→</i><b>DECIDE</b><i>→</i><span>EXECUTE</span></div>
        <div className="score-grid"><DuoScore label="相关性" value={decision.scores.relevance} /><DuoScore label="营销感" value={decision.scores.sales} inverted /><DuoScore label="重复风险" value={decision.scores.repeat} inverted /></div>
        <div className="signal-chips">{decision.signals.map((signal) => <span key={signal}>{signal}</span>)}</div>
        {decision.probabilities && <DuoProbabilityStrip probabilities={decision.probabilities} />}
      </div>
      <DuoAutomation enabled={automationEnabled} autoSkip={autoSkip} autoSave={autoSave} autoKeep={autoKeep} onToggle={onToggleAutomation} onToggleSkip={onToggleAutoSkip} onToggleSave={onToggleAutoSave} onToggleKeep={onToggleAutoKeep} />
      <div className="apple-action-feedback"><span className={`duo-execute-dot ${decisionColor}`} /><strong>{feedback}</strong><small>{decisionReady ? 'Jev 的决定会同步到左屏' : '先读评论，再输出一个动作'}</small></div>
      <div className="duo-action-feedback"><span className={`duo-execute-dot ${decisionColor}`} /><strong>{isThinking ? 'Jev 正在比较评论信号' : actionFeedback || `Action adapter: ${decision.action}`}</strong><small>{isThinking ? '等待一个可执行的判断' : '输出是动作，左屏会同步执行'}</small></div>
      <div className="duo-compact-stream"><span><b>{stats.scanned}</b> SCANS</span><span><b>{stats.saved}</b> SAVED</span><span><b>{stats.skipped}</b> SKIPPED</span><span><b>{stats.human}</b> HUMAN</span><strong>{decisionLog[0] ? `LAST · ${decisionLog[0].action}` : 'DECISION STREAM READY'}</strong></div>
      <div className="duo-execute-note"><span className={`duo-execute-dot ${decisionColor}`} /> <strong>{isThinking ? 'Jev 正在比较评论信号' : `Action adapter: ${decision.action}`}</strong><small>{isThinking ? '等待一个可执行的判断' : '输出是动作，不是长篇解释'}</small></div>
      <button type="button" className="run-button duo-run-button" onClick={onRun} disabled={isThinking}><FontAwesomeIcon icon={isThinking ? faRotate : faPlay} spin={isThinking} /> {isThinking ? 'Jev 正在判断' : '让 Jev 决定下一步'}</button>
      {apiStatus === 'live' && <div className="api-note live-note">已收到 Jev 实时判断 · key 只在本地代理使用</div>}
      {apiStatus === 'error' && <div className="api-note error-note">实时 API 暂不可用，当前显示 fixture：{apiError}</div>}
      <div className="stats-row"><DuoStat value={stats.scanned} label="已判断" /><DuoStat value={stats.skipped} label="已跳过" /><DuoStat value={stats.saved} label="已收藏" /><DuoStat value={stats.human} label="交给人" /></div>
      <div className="decision-log"><div className="log-heading"><span>DECISION STREAM</span><span>LIVE</span></div>{decisionLog.length === 0 && <div className="empty-log">点击按钮，观察 Jev 如何把评论区变成下一步动作。</div>}{decisionLog.map((item) => <div className="log-item" key={item.id}><span className={`log-dot ${actionColors[item.action]}`} /><div><strong>{item.label}</strong><small>{item.title}</small></div><b>{Math.round(item.confidence * 100)}%</b></div>)}</div>
    </div>
  );
}

function DuoAutomation({ enabled, autoSkip, autoSave, autoKeep, onToggle, onToggleSkip, onToggleSave, onToggleKeep }) {
  return <div className="duo-automation-card">
    <div className="duo-automation-head"><div><span>AUTO MODE</span><strong>让 Jev 自动执行动作</strong></div><AppleToggle checked={enabled} onChange={onToggle} label="全自动" primary /></div>
    <div className={`duo-automation-rules ${enabled ? '' : 'is-disabled'}`}>
      <AppleToggle checked={autoSkip} onChange={onToggleSkip} disabled={!enabled} label="跳过" />
      <AppleToggle checked={autoSave} onChange={onToggleSave} disabled={!enabled} label="收藏" />
      <AppleToggle checked={autoKeep} onChange={onToggleKeep} disabled={!enabled} label="认真看" />
    </div>
  </div>;
}

function AppleToggle({ checked, onChange, disabled = false, label, primary = false }) {
  return <button type="button" className={`apple-toggle-row ${disabled ? 'is-disabled' : ''}`} role="switch" aria-checked={checked} aria-label={label} onClick={() => onChange(!checked)} disabled={disabled}>
    <span>{label}</span><i className={`apple-switch ${checked ? 'is-on' : ''} ${primary ? 'is-primary' : ''}`}><b /></i>
  </button>;
}

function DuoScore({ label, value, inverted = false }) {
  const display = inverted ? 1 - value : value;
  return <div className="score-item"><div><span>{label}</span><b>{Math.round(display * 100)}</b></div><div className="score-track"><i style={{ width: `${display * 100}%` }} /></div></div>;
}

function DuoProbabilityStrip({ probabilities }) {
  return <div className="live-probabilities"><span>LIVE PROBABILITY</span>{Object.values(JEV_STATES).map((state) => <b key={state}>{state}<em>{Math.round(Number(probabilities[state] || 0) * 100)}%</em></b>)}</div>;
}

function DuoStat({ value, label }) {
  return <div><strong>{value}</strong><span>{label}</span></div>;
}

function DuoCommentsDrawer({ video, onClose }) {
  return <div className="comments-backdrop" onClick={onClose}><section className="comments-drawer" onClick={(event) => event.stopPropagation()}><div className="drawer-handle" /><div className="drawer-header"><strong>评论 {video.comments}</strong><button type="button" onClick={onClose} aria-label="关闭评论"><FontAwesomeIcon icon={faXmark} /></button></div><div className="drawer-tabs"><span className="active">全部</span><span>最热</span></div><div className="drawer-note">Jev 读取最高赞评论，把它当成视频的第二层语境。</div><div className="comment-list">{video.commentsData.map((comment, index) => <div className={`comment-row ${index === 0 ? 'top-comment-row' : ''}`} key={`${comment.user}-${comment.text}`}><div className="comment-avatar">{comment.user.slice(1, 2).toUpperCase()}</div><div className="comment-content"><div className="comment-user-row"><strong>{comment.user}</strong><small>{index === 0 ? '刚刚' : '1小时前'}</small></div><p>{comment.text}</p><div className="comment-meta"><span>{index === 0 && <em>最高赞样本</em>} 回复</span><button type="button" aria-label="赞评论"><FontAwesomeIcon icon={faHeart} /> {comment.likes}</button></div></div></div>)}</div><div className="drawer-composer"><span>添加评论…</span><small>☺</small><small>◉</small><FontAwesomeIcon icon={faArrowUp} rotation={45} /></div></section></div>;
}
